import { useEffect, useState, useCallback } from "react";
import { Mail, Eye, Send, Save, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const AUD_STYLE = {
  Customer: "bg-blue-100 text-blue-700", Seller: "bg-purple-100 text-purple-700",
  Admin: "bg-amber-100 text-amber-700", Recipient: "bg-teal-100 text-teal-700",
};

export function AdminEmailManager() {
  const [types, setTypes] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [preview, setPreview] = useState(null); // {id, label, html}
  const [testFor, setTestFor] = useState(null); // {id, label}
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/email/types").then((r) => {
      setTypes(r.data.types || []);
      const d = {};
      (r.data.types || []).forEach((t) => { d[t.id] = t.subject_override || ""; });
      setDrafts(d);
    }).catch((e) => { toast.error(formatApiError(e?.response?.data?.detail)); setTypes([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t, val) => {
    setTypes((ts) => ts.map((x) => x.id === t.id ? { ...x, enabled: val } : x));
    try { await api.put(`/admin/email/types/${t.id}`, { enabled: val }); toast.success(`${t.label} ${val ? "enabled" : "disabled"}`); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); load(); }
  };
  const saveSubject = async (t) => {
    try { await api.put(`/admin/email/types/${t.id}`, { subject_override: drafts[t.id] }); toast.success("Subject saved"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const openPreview = async (t) => {
    try {
      const r = await api.get(`/admin/email/types/${t.id}/preview`, { responseType: "text" });
      setPreview({ id: t.id, label: t.label, html: r.data });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const sendTest = async () => {
    setSending(true);
    try {
      const r = await api.post(`/admin/email/types/${testFor.id}/test`, testTo ? { to: testTo } : {});
      toast.success(`Test email sent to ${r.data.sent_to}`);
      setTestFor(null); setTestTo("");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const sendAll = async () => {
    setSendingAll(true);
    try {
      const r = await api.post("/admin/email/test-all", {});
      toast.success(`Sent ${r.data.sent}/${r.data.total} sample emails to ${r.data.sent_to}`, {
        description: r.data.failed ? `${r.data.failed} couldn't be delivered.` : "Check your inbox to review the whole suite.",
      });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSendingAll(false); }
  };

  return (
    <div data-testid="admin-email-manager">
      <PageHeader title="Email Manager" icon={Mail}
        subtitle="Preview, enable/disable and send-test every transactional email. Disabling a type stops it from being sent; a custom subject overrides the default." />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <span className="text-sm text-slate-600">Review the entire email suite in one go — a sample of every type lands in your inbox.</span>
        <button onClick={sendAll} disabled={sendingAll} data-testid="send-all-samples-btn" className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          <Send className="h-4 w-4" /> {sendingAll ? "Sending 22 samples…" : "Email me all 22 samples"}
        </button>
      </div>

      {!types ? null : types.length === 0 ? (
        <p className="text-sm text-slate-500">No email types registered.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="email-types-table">
            <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="p-3">Email type</th><th className="p-3">Audience</th><th className="p-3">Subject (editable override)</th><th className="p-3 text-center">Enabled</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t.id} className="border-b border-slate-50 align-top hover:bg-slate-50/60" data-testid={`email-type-${t.id}`}>
                  <td className="p-3 font-semibold text-slate-800">{t.label}</td>
                  <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${AUD_STYLE[t.audience] || "bg-slate-100 text-slate-600"}`}>{t.audience}</span></td>
                  <td className="p-3">
                    <Input value={drafts[t.id] ?? ""} onChange={(e) => setDrafts((d) => ({ ...d, [t.id]: e.target.value }))}
                      placeholder={t.default_subject} className="min-w-[240px]" data-testid={`subject-${t.id}`} />
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-[11px] text-slate-400">Default: {t.default_subject}</span>
                      {(drafts[t.id] || "") !== (t.subject_override || "") && (
                        <button onClick={() => saveSubject(t)} data-testid={`save-subject-${t.id}`} className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white"><Save className="h-3 w-3" /> Save</button>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-center"><Switch checked={t.enabled} onCheckedChange={(v) => toggle(t, v)} data-testid={`toggle-${t.id}`} /></td>
                  <td className="p-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openPreview(t)} data-testid={`preview-${t.id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Eye className="h-3.5 w-3.5" /> Preview</button>
                      <button onClick={() => { setTestFor(t); setTestTo(""); }} data-testid={`test-${t.id}`} className="inline-flex items-center gap-1 rounded-full brand-gradient-bg px-3 py-1.5 text-xs font-semibold text-white"><Send className="h-3.5 w-3.5" /> Test</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreview(null)} data-testid="email-preview-modal">
          <div className="flex max-h-[90vh] w-full max-w-[680px] flex-col overflow-hidden rounded-2xl bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 p-3">
              <span className="text-sm font-semibold text-slate-700">Preview · {preview.label}</span>
              <button onClick={() => setPreview(null)} data-testid="close-preview" className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <iframe title="email-preview" srcDoc={preview.html} className="h-[70vh] w-full border-0" data-testid="email-preview-frame" />
          </div>
        </div>
      )}

      {testFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setTestFor(null)} data-testid="email-test-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">Send a test · {testFor.label}</span>
              <button onClick={() => setTestFor(null)} className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <p className="mb-2 text-xs text-slate-500">Leave blank to send to your own admin email.</p>
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="name@example.com" data-testid="test-email-input" />
            <button onClick={sendTest} disabled={sending} data-testid="send-test-btn" className="mt-4 w-full rounded-full brand-gradient-bg px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{sending ? "Sending…" : "Send test email"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
